import React from 'react'
import {
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { createEmptyItem } from '../CustomerOnboardingWizard'
import { deviceSetupShapes } from 'utils/constant'
import FormField from 'layouts/applications/wizard/components/FormField'

// field_type_id mapping:
// 1 = text  |  2 = number  |  3 = select  |  4 = boolean/switch  |  null = text fallback

function DynamicAttributeField({ attr, value, onChange }) {
  const fieldType = attr.field_type_id
  const label = attr.label || attr.attribute_name

  const inputSx = {
    '& .MuiInputBase-input': {
      height: 40,
      minHeight: 40,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      padding: '16.5px 14px',
    },
  }
  const inputProps = { sx: { height: 40, minHeight: 40 } }

  if (fieldType === 4) {
    // Boolean / switch
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            color="primary"
          />
        }
        label={label}
      />
    )
  }

  return (
    label.includes('Shape') ? <TextField
      select
      fullWidth
      label="Shape"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      InputProps={{ sx: { height: 40, minHeight: 40 } }}
      sx={{
        '& .MuiSelect-select, & .MuiInputBase-input': {
          height: 40,
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      {deviceSetupShapes.map((s, i) => (
        <MenuItem
          key={s}
          value={s}
          sx={{ height: 40, minHeight: 40, display: 'flex', alignItems: 'center' }}
        >
          {s}
        </MenuItem>
      ))}
    </TextField> : <TextField
      fullWidth
      label={label}
      type={fieldType === 2 ? 'number' : 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      InputProps={inputProps}
      sx={inputSx}
    />
  )
}

function ItemsStep({ items, setItems, dropdowns }) {
  const itemOptions = dropdowns.items || []
  const icons = dropdowns.icons || []
  const statusOptions = dropdowns.entityStatusOptions || []

  const updateItem = (id, field, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)))
  }

  const updateAttribute = (id, attributeId, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? { ...it, attributes: { ...it.attributes, [attributeId]: value } }
          : it,
      ),
    )
  }

  const handleItemTypeChange = (id, itemId) => {
    // When the item type changes, reset attributes to empty map
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, item_id: itemId, attributes: {} } : it,
      ),
    )
  }

  const addItem = () => {
    setItems((prev) => [...prev, createEmptyItem()])
  }

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }

  return (
    <MDBox mt={2}>
      {items.length === 0 && (
        <MDTypography variant="body2" color="text" mb={2}>
          This step is optional. You can link tanks / assets now or skip and finish later.
        </MDTypography>
      )}

      {items.map((item, idx) => {
        // Get attributes for the selected item type
        const selectedOption = itemOptions.find(
          (opt) => String(opt.item_id) === String(item.item_id),
        )
        const dynamicAttributes = selectedOption?.Attributes || []

        return (
          <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  Item #{idx + 1}
                </MDTypography>
                <MDButton
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => removeItem(item.id)}
                >
                  Remove
                </MDButton>
              </MDBox>

              <Grid container spacing={2}>
                {/* ── Core fields ── */}
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Item"
                    value={item.item_id}
                    onChange={(e) => handleItemTypeChange(item.id, e.target.value)}
                    size="small"
                    InputProps={{ sx: { height: 40, minHeight: 40 } }}
                    sx={{
                      '& .MuiSelect-select, & .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    {itemOptions.map((opt) => (
                      <MenuItem
                        key={opt.item_id}
                        value={opt.item_id}
                        sx={{ height: 40, minHeight: 40, display: 'flex', alignItems: 'center' }}
                      >
                        {opt.item_name || opt.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={item.name}
                    onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                    size="small"
                    InputProps={{ sx: { height: 40, minHeight: 40 } }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16.5px 14px',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Map Identifier"
                    value={item.map_identifier}
                    onChange={(e) => updateItem(item.id, 'map_identifier', e.target.value)}
                    size="small"
                    InputProps={{ sx: { height: 40, minHeight: 40 } }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16.5px 14px',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Address (by index)"
                    value={item.address_index}
                    onChange={(e) =>
                      updateItem(item.id, 'address_index', Number(e.target.value) || 0)
                    }
                    size="small"
                    InputProps={{ sx: { height: 40, minHeight: 40 } }}
                    sx={{
                      '& .MuiSelect-select, & .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    {Array.from({ length: 10 }).map((_, i) => (
                      <MenuItem
                        key={i}
                        value={i}
                        sx={{ height: 40, minHeight: 40, display: 'flex', alignItems: 'center' }}
                      >
                        Address #{i + 1}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Icon"
                    value={item.icon_id}
                    onChange={(e) => updateItem(item.id, 'icon_id', e.target.value)}
                    size="small"
                    InputProps={{ sx: { height: 40, minHeight: 40 } }}
                    sx={{
                      '& .MuiSelect-select, & .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    {icons.map((icon) => (
                      <MenuItem
                        key={icon.icon_id}
                        value={icon.icon_id}
                        sx={{ height: 40, minHeight: 40, display: 'flex', alignItems: 'center' }}
                      >
                        <i className={icon.class_name} style={{ paddingRight: '5px' }}></i>
                        {icon.icon_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={item.status_type_id}
                    onChange={(e) => updateItem(item.id, 'status_type_id', e.target.value)}
                    size="small"
                    InputProps={{ sx: { height: 40, minHeight: 40 } }}
                    sx={{
                      '& .MuiSelect-select, & .MuiInputBase-input': {
                        height: 40,
                        minHeight: 40,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    {statusOptions.map((s) => (
                      <MenuItem
                        key={s.status_option_id}
                        value={s.status_type_id}
                        sx={{ height: 40, minHeight: 40, display: 'flex', alignItems: 'center' }}
                      >
                        {s.StatusType.status_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(item.is_default)}
                        onChange={(e) => updateItem(item.id, 'is_default', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Default address item for this entity"
                  />
                </Grid>

                {/* ── Dynamic attributes from the selected item type ── */}
                {dynamicAttributes.length > 0 && (
                  <Grid item xs={12}>
                    <MDTypography variant="caption" fontWeight="medium" color="text" mb={1} display="block">
                      Item Attributes
                    </MDTypography>
                  </Grid>
                )}
                {dynamicAttributes.map((attr) => (
                  <Grid item xs={12} md={4} key={attr.attribute_id}>
                    <DynamicAttributeField
                      attr={attr}
                      value={item.attributes?.[attr.attribute_id] ?? ''}
                      onChange={(val) => updateAttribute(item.id, attr.attribute_id, val)}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )
      })}

      <MDButton variant="outlined" color="info" size="small" onClick={addItem}>
        Add Item
      </MDButton>
    </MDBox>
  )
}

export default ItemsStep
